
import { useTranslation } from "react-i18next"
import { useEffect, useState } from "react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FilterX } from "lucide-react"
import type { ReviewFilters } from "@/api/reviews"
import { getGyms, type Gym } from "@/api/gyms"

interface ReviewFiltersProps {
  filters: ReviewFilters
  onFiltersChange: (filters: ReviewFilters) => void
}

export function ReviewFiltersComponent({ filters, onFiltersChange }: ReviewFiltersProps) {
  const { t } = useTranslation()
  const [gyms, setGyms] = useState<Gym[]>([])
  const [loadingGyms, setLoadingGyms] = useState(true)

  useEffect(() => {
    const fetchGyms = async () => {
      try {
        const gymsData = await getGyms()
        setGyms(gymsData)
      } catch (error) {
        console.error('Error fetching gyms:', error)
      } finally {
        setLoadingGyms(false)
      }
    }
    fetchGyms()
  }, [])

  const handleFilterChange = (key: keyof ReviewFilters, value: string) => {
    // Convert "all" back to empty string for API
    const apiValue = value === 'all' ? '' : value
    const newFilters = { ...filters, [key]: apiValue, page: 1 }
    onFiltersChange(newFilters)
  }

  const handleClearFilters = () => {
    onFiltersChange({
      page: 1,
      limit: 20,
      gymId: '',
      reviewType: '',
      minRating: undefined,
      startDate: '',
      endDate: '',
    })
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* Gym Filter */}
        <div className="space-y-2">
          <Label>{t('reviews.filterByGym')}</Label>
          <Select
            value={filters.gymId || 'all'}
            onValueChange={(value) => handleFilterChange('gymId', value)}
            disabled={loadingGyms}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('reviews.allGyms')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('reviews.allGyms')}</SelectItem>
              {gyms.map((gym) => (
                <SelectItem key={gym._id} value={gym._id}>
                  {gym.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Review Type Filter */}
        <div className="space-y-2">
          <Label>{t('reviews.filterByType')}</Label>
          <Select
            value={filters.reviewType || 'all'}
            onValueChange={(value) => handleFilterChange('reviewType', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('reviews.allTypes')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('reviews.allTypes')}</SelectItem>
              <SelectItem value="class">{t('reviews.classReviews')}</SelectItem>
              <SelectItem value="staff">{t('reviews.staffReviews')}</SelectItem>
              <SelectItem value="class_session">{t('reviews.sessionReviews')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Min Rating Filter */}
        <div className="space-y-2">
          <Label>{t('reviews.filterByRating')}</Label>
          <Select
            value={filters.minRating?.toString() || 'all'}
            onValueChange={(value) => handleFilterChange('minRating', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('reviews.allRatings')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('reviews.allRatings')}</SelectItem>
              <SelectItem value="5">{t('reviews.fiveStarsOnly')}</SelectItem>
              <SelectItem value="4">{t('reviews.fourPlusStars')}</SelectItem>
              <SelectItem value="3">{t('reviews.threePlusStars')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Start Date Filter */}
        <div className="space-y-2">
          <Label>{t('reviews.startDate')}</Label>
          <Input
            type="date"
            value={filters.startDate || ''}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </div>

        {/* End Date Filter */}
        <div className="space-y-2">
          <Label>{t('reviews.endDate')}</Label>
          <Input
            type="date"
            value={filters.endDate || ''}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </div>
      </div>

      {/* Clear Filters Button */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={handleClearFilters}
          className="gap-2"
        >
          <FilterX className="h-4 w-4" />
          {t('reviews.clearFilters')}
        </Button>
      </div>
    </div>
  )
}
